import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@notino/react-styleguide';
import {
  AvailabilityState,
  CatalogVariantFragmentFragment,
  GetCatalogProductViewQuery,
} from '@notino/shared/definitions/types';

import { Flex } from '@components/Flex';
import { useFetchProductAlternatives } from '@containers/ProductDetailContainer/hooks/useFetchProductAlternatives';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { Watchdog } from '@sharedComponents/Watchdog';
import { trackWatchdogClick } from '@sharedComponents/Watchdog/tracking';
import { useWatchdogActive } from '@sharedComponents/Watchdog/useWatchdogActive';
import { watchdogMessages } from '@sharedComponents/Watchdog/watchdogMessages';

import { SimilarProductsButton } from './SimilarProductsButton';

interface WatchdogAndSimilarProductsProps {
  currentVariant: CatalogVariantFragmentFragment;
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
}

export const WatchdogAndSimilarProducts: React.FC<
  WatchdogAndSimilarProductsProps
> = ({ currentVariant, product }) => {
  const { experiment_pd_outofstock_product_alternatives } = useFeatureFlags();
  const { data } = useFetchProductAlternatives();

  const { watchdogActive, showWatchdog } = useWatchdogActive({
    variant: currentVariant,
    cacheOnly: false,
  });

  const everyProductUnavailable = product.variants.every(
    (v) => v.availability.state !== AvailabilityState.CanBeBought
  );

  const alreadyFetchedButNoProducts =
    data &&
    Boolean(data.getOutOfStockAlternatives?.recommendedProducts?.length) ===
      false;

  const shouldShowSimilarProductsButton =
    experiment_pd_outofstock_product_alternatives &&
    !alreadyFetchedButNoProducts &&
    everyProductUnavailable;

  if (!shouldShowSimilarProductsButton && !showWatchdog) {
    return null;
  }

  return (
    <Flex gap={20} margin="1rem 0">
      <Watchdog variant={currentVariant} id="pdWatchdog">
        <Button
          onClick={() => trackWatchdogClick(product, currentVariant)}
          data-testid="pd-watchdog"
        >
          <FormattedMessage
            {...(watchdogActive
              ? watchdogMessages.watchdogActive
              : watchdogMessages.watchAvailability)}
          />
        </Button>
      </Watchdog>

      <SimilarProductsButton />
    </Flex>
  );
};
